import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import MiniBarChart from "./MiniBarChart";
import { getHeaderTextStyle, calculateYAxisDomain, hasLowNSize, formatDataLabel, useYAxisWidth, splitHierarchy } from "../chartHelpers";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { AxisLabel } from "../../components/AxisLabel";
import { ChartContainer } from "../../components/ChartContainer";
import { ChartFootnote } from "../../components/ChartFootnote";
import { EllipsisText } from "../../components/EllipsisText";
import { NSizeLabel } from "../../components/NSizeLabel";
import { chartConfig } from "../../chartConfig";
import ChartHeader from "../../components/ChartHeader";
import ChartTabularView from "../../components/ChartTabularView";
import { RightAlignedEllipsisText } from "../../components/RightAlignedEllipsisText";
import { ChartRenderedFromType, IHierarchyDef, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { XAxisScale } from "../../components/XAxisScale";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { useScaledConfig } from "../../ChartScaleContext";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import AskKuralFootnote from "../../components/AskKuralFootnote";
import ChartError from "../../ChartError";

// Defensive upper bound to prevent unbounded iteration over response-controlled
// hierarchy paths. Set comfortably above any realistic hierarchy depth so
// legitimate data is never truncated.
const MAX_LOOP_ITERATIONS = 100000;

/* -------------------- Types -------------------- */
type FlatColumn = {
	path: string[];
	leafData: any[];
};

type HeaderCell = {
	label: string;
	colSpan: number;
	levelIndex: number;
};

interface HClusteredBarChartProps {
	chartResponse: any;
	chartRules?: Record<string, any>;
	yAxisMinWidth?: number;
	yAxisMaxWidth?: number;
	yAxisWidth?: number;
	isAISummaryAvailable?: boolean;
	isChartView?: boolean;
	setIsChartView?: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}

type CellType = "header" | "nsize" | "attrLabel" | "data";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	totalRows: number;
	totalCols: number;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

/* -------------------- Authoritative Order Resolvers -------------------- */
/**
 * Reorder discovered keys at a 0-based nesting depth using only column-oriented
 * filter_hierarchy entries. Caller passes `columnHierarchies` already filtered
 * + sorted by `splitHierarchy`. Stragglers (keys present in data but missing
 * from values) are appended in original discovery order so nothing is dropped.
 */
const resolveKeysByHierarchyOrder = (
	discoveredKeys: string[],
	columnHierarchies: IHierarchyDef[] | undefined,
	depth: number,
): string[] => {
	if (!columnHierarchies || columnHierarchies.length === 0) return discoveredKeys;

	// Sort defensively by `order` ascending (matches VClustered / VStacked / chartTableTransformer).
	const sorted = [...columnHierarchies].sort((a, b) => a.order - b.order);
	const hierarchyAtDepth = sorted[depth];
	if (!hierarchyAtDepth || !Array.isArray(hierarchyAtDepth.values) || hierarchyAtDepth.values.length === 0) {
		return discoveredKeys;
	}

	const remaining = new Set(discoveredKeys);
	const ordered: string[] = [];

	hierarchyAtDepth.values.forEach((val) => {
		if (remaining.has(val)) {
			ordered.push(val);
			remaining.delete(val);
		}
	});

	discoveredKeys.forEach((k) => {
		if (remaining.has(k)) ordered.push(k);
	});

	return ordered;
};

/**
 * Reorder a discovered list against a single hierarchy's `values` array.
 * Stragglers (in data but not in values) are appended in discovery order.
 */
const reorderByValues = (discovered: string[], values: string[] | undefined): string[] => {
	if (!values || values.length === 0) return discovered;
	const remaining = new Set(discovered);
	const ordered: string[] = [];
	values.forEach((v) => {
		if (remaining.has(v)) {
			ordered.push(v);
			remaining.delete(v);
		}
	});
	discovered.forEach((d) => {
		if (remaining.has(d)) ordered.push(d);
	});
	return ordered;
};

/* -------------------- Helpers -------------------- */
/**
 * Flatten nested chart_data into one row per (column path, leaf array).
 * Reorders nested object keys at each depth via columnHierarchies, and
 * reorders the leaf array's items by xAxis using either rowHierarchy
 * (when present) or the deepest column hierarchy (when all dims are column).
 */
const buildFlatColumns = (
	dataNode: any,
	columnHierarchies: IHierarchyDef[] | undefined,
	rowHierarchy: IHierarchyDef | null | undefined,
	currentPath: string[] = [],
): FlatColumn[] => {
	if (Array.isArray(dataNode)) {
		// Authoritative xAxis order: rowHierarchy values, else column hierarchy
		// at this nesting depth (the dim the backend emitted as xAxis).
		const sortedCols = columnHierarchies ? [...columnHierarchies].sort((a, b) => a.order - b.order) : [];
		const orderingValues = rowHierarchy?.values ?? sortedCols[currentPath.length]?.values;

		if (!orderingValues || orderingValues.length === 0) {
			return [{ path: currentPath, leafData: dataNode }];
		}

		const itemMap = new Map<string, any>();
		dataNode.forEach((row: any) => itemMap.set(row.xAxis, row));

		const orderedItems: any[] = [];
		const seen = new Set<string>();

		orderingValues.forEach((v) => {
			if (itemMap.has(v) && !seen.has(v)) {
				orderedItems.push(itemMap.get(v));
				seen.add(v);
			}
		});
		// Append stragglers (in data but not in hierarchy values) in discovery order.
		dataNode.forEach((row: any) => {
			if (!seen.has(row.xAxis)) {
				orderedItems.push(row);
				seen.add(row.xAxis);
			}
		});

		return [{ path: currentPath, leafData: orderedItems }];
	}

	// Reorder keys at this nesting depth using column-oriented filter_hierarchy.
	const orderedKeys = resolveKeysByHierarchyOrder(Object.keys(dataNode), columnHierarchies, currentPath.length);

	let columns: FlatColumn[] = [];
	orderedKeys.forEach((key) => {
		columns = columns.concat(buildFlatColumns(dataNode[key], columnHierarchies, rowHierarchy, [...currentPath, key]));
	});
	return columns;
};

const buildHeaderRow = (columns: FlatColumn[], levelIndex: number): HeaderCell[] => {
	const headers: HeaderCell[] = [];
	if (columns.length === 0) return headers;

	let currentLabel = columns[0].path[levelIndex];
	let currentSpan = 0;
	let prevCol: FlatColumn | null = null;

	columns.forEach((col, i) => {
		const label = col.path[levelIndex];
		let parentChanged = false;
		if (levelIndex > 0 && prevCol) {
			for (let p = 0; p < levelIndex; p++) {
				if (col.path[p] !== prevCol.path[p]) {
					parentChanged = true;
					break;
				}
			}
		}
		if (i > 0 && (label !== currentLabel || parentChanged)) {
			headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
			currentLabel = label;
			currentSpan = 1;
		} else {
			currentSpan++;
		}
		prevCol = col;
	});
	headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
	return headers;
};

const buildCellMap = (columns: FlatColumn[]) => {
	const map = new Map<string, any>();
	columns.forEach((col) => {
		const pathKey = col.path.join("|");
		col.leafData.forEach((row: any) => {
			map.set(`${row.xAxis}|${pathKey}`, row.series);
		});
	});
	return map;
};

const buildNSizeMap = (columns: FlatColumn[]) => {
	const map = new Map<string, { min: number; max: number }>();

	const isNoDataSentinel = (v: unknown) =>
		v === null || v === undefined || (typeof v === "string" && v.toLowerCase() === "no data");

	const collectFromSeries = (row: any, values: number[]) => {
		if (!Array.isArray(row?.series)) return false;
		let found = false;
		row.series.forEach((s: any) => {
			if (isNoDataSentinel(s?.n_size)) return;
			const n = Number(s.n_size);
			if (!isNaN(n) && n > 0) {
				values.push(n);
				found = true;
			}
		});
		return found;
	};

	columns.forEach((col) => {
		const pathKey = col.path.join("|");
		const values: number[] = [];

		col.leafData.forEach((row: any) => {
			// Prefer per-series n_size so we can exclude 0 / "No data" entries
			// that get folded into row-level { min: 0, max: X } aggregates.
			if (collectFromSeries(row, values)) return;

			const val = row.n_size;
			if (isNoDataSentinel(val)) return;
			if (typeof val === "object") {
				const rMin = Number(val.min);
				const rMax = Number(val.max);
				if (!isNaN(rMin) && rMin > 0) values.push(rMin);
				if (!isNaN(rMax) && rMax > 0) values.push(rMax);
			} else {
				const n = Number(val);
				if (!isNaN(n) && n > 0) values.push(n);
			}
		});

		if (values.length > 0) {
			map.set(pathKey, { min: Math.min(...values), max: Math.max(...values) });
		}
	});
	return map;
};

const buildSeriesMaxMap = (columns: FlatColumn[], visibleWaves: string[]) => {
	const map = new Map<string, number>();

	columns.forEach((col) => {
		const pathKey = col.path.join("|");
		let maxVal = 0;

		col.leafData.forEach((row: any) => {
			if (row.series) {
				row.series.forEach((s: any) => {
					if (visibleWaves?.includes(s.name) && typeof s.value === "number") {
						if (s.value > maxVal) maxVal = s.value;
					}
				});
			}
		});
		map.set(pathKey, maxVal);
	});
	return map;
};

const getLeftBorderStyle = (columns: FlatColumn[], i: number, colorsConfig: any): React.CSSProperties => {
	if (i === 0) return { borderLeftWidth: 1, borderLeftStyle: "solid", borderLeftColor: colorsConfig.borders.solid };
	const col = columns[i];
	const prevCol = columns[i - 1];

	if (!col.path.length) return {};

	if (col.path[0] !== prevCol.path[0]) {
		return { borderLeftWidth: 1, borderLeftStyle: "solid", borderLeftColor: colorsConfig.borders.solid };
	}

	for (let l = 1; l < col.path.length - 1; l++) {
		if (col.path[l] !== prevCol.path[l]) {
			return { borderLeftWidth: 1, borderLeftStyle: "dashed", borderLeftColor: colorsConfig.borders.dashed };
		}
	}

	return { borderLeftWidth: 1, borderLeftStyle: "solid", borderLeftColor: "transparent" };
};

export const getResponsiveScale = (): number => {
	if (typeof window === "undefined") return 1;

	const config = chartConfig.getResponsiveConfig();
	const width = window.innerWidth;

	if (width < config.breakpoints.mobile) return config.scales.mobile;
	if (width < config.breakpoints.tablet) return config.scales.tablet;
	return config.scales.desktop;
};

export const calculateResponsiveYAxisWidth = (
	attributes: string[],
	scaleOverride?: number, // ← NEW
): number => {
	const config = chartConfig.getHorizontalClusteredConfig();
	const typographyConfig = chartConfig.getTypographyConfig();
	const scale = scaleOverride ?? getResponsiveScale();

	if (!attributes.length) {
		return Math.max(config.yAxisLabel.minWidth, Math.floor(config.yAxisLabel.maxWidth * scale));
	}

	const maxTextLength = Math.max(...attributes.map((attr) => attr?.length ?? 0));

	const BASE_PADDING = 32;
	const scaledPadding = Math.round(BASE_PADDING * scale);

	const baseWidth = maxTextLength * typographyConfig.charWidth + scaledPadding;
	const scaledWidth = Math.floor(baseWidth * scale);

	return Math.min(Math.max(scaledWidth, config.yAxisLabel.minWidth), config.yAxisLabel.maxWidth);
};

export const useResponsiveYAxisWidth = (
	attributes: string[],
	customWidth?: number,
	customMin?: number,
	customMax?: number,
	scaleFactor?: number, // ← NEW — pass scaled.scaleFactor for multi-tile
) => {
	const [viewportScale, setViewportScale] = useState<number>(1);

	useEffect(() => {
		const handleResize = () => {
			setViewportScale(getResponsiveScale());
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Use tile scaleFactor when available, otherwise viewport scale
	const effectiveScale = scaleFactor ?? viewportScale;

	return useMemo(() => {
		const config = chartConfig.getHorizontalClusteredConfig();
		const minWidth = customMin ?? config.yAxisLabel.minWidth;
		const maxWidth = customMax ?? config.yAxisLabel.maxWidth;

		if (customWidth) {
			return Math.min(Math.max(customWidth, minWidth), maxWidth);
		}

		// Pass effectiveScale so the calculation uses tile-level
		// scaling in multi-tile, viewport scaling in single chart
		const calculatedWidth = calculateResponsiveYAxisWidth(
			attributes,
			effectiveScale, // ← forwards tile or viewport scale
		);
		return Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
	}, [attributes, effectiveScale, customWidth, customMin, customMax]);
};

/* -------------------- Border Helpers -------------------- */
const createBorderConfig = (colorsConfig: any) => {
	const solidBorder = `1px solid ${colorsConfig.borders.solid}`;
	const dashedBorder = `1px dashed ${colorsConfig.borders.dashed}`;
	const lightBorder = `1px solid ${colorsConfig.borders.lightGray}`;

	return {
		solid: solidBorder,
		dashed: dashedBorder,
		light: lightBorder,
	};
};

const getCellBorders = (
	cellType: CellType,
	position: ICellPosition,
	borderConfig: { solid: string; dashed: string; light: string },
	columns: FlatColumn[],
	hierarchyLength: number,
): IBorderConfig => {
	const { rowIndex, colIndex, totalRows } = position;
	const isFirstCol = colIndex === 0;
	const isLastRow = rowIndex === totalRows - 1;

	// Determine left border based on hierarchy path changes
	const getLeftBorder = (): string | undefined => {
		if (isFirstCol) return undefined;

		const col = columns[colIndex];
		const prevCol = columns[colIndex - 1];

		if (!col?.path?.length || !prevCol?.path?.length) return undefined;

		// Find at which level the hierarchy changed
		for (let level = 0; level < col.path.length && level < MAX_LOOP_ITERATIONS; level++) {
			if (col.path[level] !== prevCol.path[level]) {
				// Level 0 or 1 change = solid border (top 2 hierarchies)
				// Level 2+ change = dashed border (3rd hierarchy onwards)
				return level < 2 ? borderConfig.solid : borderConfig.dashed;
			}
		}

		// No change in any hierarchy level - no border needed
		return undefined;
	};

	const getDataLeftBorder = (): string | undefined => {
		if (isFirstCol) return undefined;
		const col = columns[colIndex];
		const prevCol = columns[colIndex - 1];
		if (!col?.path?.length || !prevCol?.path?.length) return undefined;
		for (let level = 0; level < col.path.length && level < MAX_LOOP_ITERATIONS; level++) {
			if (col.path[level] !== prevCol.path[level]) {
				return level < 2 ? borderConfig.solid : undefined;
			}
		}
		return undefined;
	};

	switch (cellType) {
		case "header":
			return {
				borderLeft: getLeftBorder(),
				borderTop: borderConfig.solid,
			};

		case "nsize":
			return {
				borderLeft: getLeftBorder(),
				borderTop: borderConfig.solid,
				borderBottom: borderConfig.solid,
			};

		case "attrLabel":
			return {
				borderRight: borderConfig.light,
			};

		case "data":
			return {
				borderLeft: getDataLeftBorder(),
				borderBottom: undefined,
				// borderBottom: isLastRow ? borderConfig.solid : undefined,
			};

		default:
			return {};
	}
};

const useBorderGetter = (colorsConfig: any, columns: FlatColumn[], totalRows: number, hierarchyLength: number) => {
	const cacheRef = useRef<Map<string, IBorderConfig>>(new Map());
	const borderConfigRef = useRef(createBorderConfig(colorsConfig));

	useEffect(() => {
		borderConfigRef.current = createBorderConfig(colorsConfig);
		cacheRef.current.clear();
	}, [colorsConfig]);

	const getBorders = useCallback(
		(cellType: CellType, rowIndex: number, colIndex: number): IBorderConfig => {
			const key = `${cellType}:${rowIndex}:${colIndex}`;

			const cached = cacheRef.current.get(key);
			if (cached !== undefined) {
				return cached;
			}

			const position: ICellPosition = {
				rowIndex,
				colIndex,
				totalRows,
				totalCols: columns.length,
			};

			const borders = getCellBorders(cellType, position, borderConfigRef.current, columns, hierarchyLength);

			cacheRef.current.set(key, borders);

			return borders;
		},
		[columns, totalRows, hierarchyLength],
	);

	return getBorders;
};

/* -------------------- Component -------------------- */
const HClusteredBarChart: React.FC<HClusteredBarChartProps> = ({
	chartResponse,
	yAxisMinWidth,
	yAxisMaxWidth,
	yAxisWidth,
	isAISummaryAvailable = false,
	isChartView,
	setIsChartView,
	renderedFrom,
}) => {
	// Get configuration
	const layoutConfig = chartConfig.getLayoutConfig();
	const dimensionsConfig = chartConfig.getDimensionsConfig();
	const spacingConfig = chartConfig.getSpacingConfig();
	const colorsConfig = chartConfig.getColorsConfig();
	const apiData = chartResponse?.data?.[0]?.chart_data;
	// Determines if chart_data is missing/empty/invalid to render fallback UI
	const isDataEmpty = useMemo(() => {
		if (apiData === null || apiData === undefined) return true;
		if (Array.isArray(apiData) && apiData.length === 0) return true;
		if (typeof apiData !== "object") return true; // guard against primitives
		if (!Array.isArray(apiData) && Object.keys(apiData).length === 0) return true;
		return false;
	}, [apiData]);
	const hierarchyDef = chartResponse?.filter_hierarchy || [];
	const hasHierarchy = hierarchyDef.length > 0;

	// Split into column-oriented (drives column header bands) and row-oriented
	// (drives y-axis attribute ordering) hierarchies.
	const { columnHierarchies, rowHierarchy } = useMemo(() => splitHierarchy(hierarchyDef), [hierarchyDef]);

	// Use optional chaining — chart_config itself may be missing
	const labelFormat = chartResponse?.chart_config?.dlabel_fmt;
	const statTestConfig: IStatTestConfig | null = chartResponse?.chart_config?.stat_test_config ?? null;
	const scaled = useScaledConfig();
	const [visibleWaves, setVisibleWaves] = useState<string[]>([]);
	const [expanded, setExpanded] = useState(false);
	const [recalcKey, setRecalcKey] = useState(0);

	const headerHeightPx = useMemo(() => {
		if (!hasHierarchy) return layoutConfig.header.rowHeight;
		return hierarchyDef.length * layoutConfig.header.rowHeight;
	}, [hasHierarchy, hierarchyDef.length, layoutConfig.header.rowHeight]);

	// Refs for dynamic height calculation
	const containerRef = useRef<HTMLDivElement>(null);
	const contentAreaRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const footnoteRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLDivElement>(null);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);
	const xAxisRef = useRef<HTMLDivElement>(null);

	const containerWidth = useContainerWidth(containerRef, recalcKey);
	const containerHeight = useContainerHeight(containerRef, recalcKey);

	// const contentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	// NEW — stabilize height to prevent resize observer feedback loop
	const ASK_KURAL_MAX_CHART_HEIGHT = 150;
	const isRenderedFromAskKural = renderedFrom === ChartRenderedFromType.AskKural;
	const isRenderedFromAskKuralInline = renderedFrom === ChartRenderedFromType.AskKuralInline;

	// Reusable stabilized height pattern — add to HClusteredBarChart, HStackedBarChart,
	// VStackedBarChart, HLineChart, VLineChart
	const rawContentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	const stableContentAreaHeightRef = useRef<number>(0);
	const measureCountRef = useRef<number>(0);

	const contentAreaHeight = useMemo(() => {
		const raw = rawContentAreaHeight || 0;
		const prev = stableContentAreaHeightRef.current;

		if (prev === 0) {
			stableContentAreaHeightRef.current = raw;
			measureCountRef.current = 1;
			return raw;
		}

		measureCountRef.current += 1;

		// Lock after 2 measurements for AskKural to prevent oscillation
		if ((isRenderedFromAskKuralInline || isRenderedFromAskKural) && measureCountRef.current > 2) {
			return prev;
		}

		// After warm-up, require a larger delta to defeat header-wrap-induced
		// oscillation. A real resize easily exceeds this threshold.
		const threshold = measureCountRef.current > 3 ? 24 : 5;
		if (Math.abs(raw - prev) > threshold) {
			stableContentAreaHeightRef.current = raw;
			return raw;
		}

		return prev;
	}, [rawContentAreaHeight, isRenderedFromAskKuralInline]);

	const tableHeaderHeight = useContainerHeight(tableHeaderRef, recalcKey);
	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef) || contentAreaHeight;
	const xAxisHeight = useContainerHeight(xAxisRef, recalcKey);
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const isEffectiveMultiTile = isMultiTileSlide || isRenderedFromAskKuralInline;

	useEffect(() => {
		setVisibleWaves(Object.keys(chartResponse?.chart_config?.ser_color ?? {}));
	}, [chartResponse?.chart_config?.ser_color]);

	useEffect(() => {
		if (isChartView) {
			// Only increment when switching TO chart view
			setRecalcKey((prev) => prev + 1);
			// Reset high-water-marks on view switch so fresh measurements are taken
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
		}
	}, [isChartView]);

	// One-shot recalc once the table header first reports a real height.
	// On initial paint, tableHeaderHeight is 0 (refs not yet measured), so
	// dynamicTableHeight is computed against a zero header and the bars
	// render at the wrong scale. Reset the contentArea stabilization and
	// bump recalcKey so every ref re-measures against the settled layout.
	const didRecalcAfterHeaderRef = useRef(false);
	useEffect(() => {
		if (didRecalcAfterHeaderRef.current) return;
		if (tableHeaderHeight > 0) {
			didRecalcAfterHeaderRef.current = true;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [tableHeaderHeight]);

	const columns = useMemo(
		() => (isDataEmpty ? [] : buildFlatColumns(apiData, columnHierarchies, rowHierarchy)),
		[apiData, columnHierarchies, rowHierarchy, isDataEmpty],
	);

	// Authoritative series order from chart_config.ser_color (consumed by ChartTabularView).
	const seriesOrder = useMemo(
		() => (chartResponse?.chart_config?.ser_color ? Object.keys(chartResponse.chart_config.ser_color) : []),
		[chartResponse?.chart_config?.ser_color],
	);

	const showLowNSize = useMemo(() => (isDataEmpty ? false : hasLowNSize(apiData, { threshold: 30 })), [apiData, isDataEmpty]);
	const hClusteredConfig = chartConfig.getHorizontalClusteredConfig();
	const MULTI_TILE_Y_MIN = hClusteredConfig.yAxisLabel.multiTileMinWidth;
	const MULTI_TILE_Y_MAX = hClusteredConfig.yAxisLabel.multiTileMaxWidth;
	// Calculate X-Axis Domain for horizontal bars
	const xAxisDomain = useMemo(() => {
		const allValues: number[] = [];
		columns.forEach((col) => {
			if (col.leafData && Array.isArray(col.leafData)) {
				col.leafData.forEach((row: any) => {
					if (row.series && Array.isArray(row.series)) {
						row.series.forEach((s: any) => {
							if (typeof s.value === "number" && isFinite(s.value)) {
								allValues.push(s.value);
							}
						});
					}
				});
			}
		});

		return calculateYAxisDomain(allValues, labelFormat);
	}, [columns, labelFormat]);

	const maxSeriesPerRow = useMemo(() => {
		if (!columns.length || !visibleWaves.length) return 1;

		let max = 1;

		columns.forEach((col) => {
			col.leafData.forEach((row: any) => {
				const count = row.series?.filter((s: any) => visibleWaves.includes(s.name)).length ?? 0;

				if (count > max) max = count;
			});
		});

		return max;
	}, [columns, visibleWaves]);

	const totalSeriesCount = useMemo(() => {
		return Object.keys(chartResponse?.chart_config?.ser_color ?? {}).length;
	}, [chartResponse?.chart_config?.ser_color]);

	const attributes = useMemo(() => {
		const discovered = columns[0]?.leafData?.map((r: any) => r.xAxis) || [];
		// When a row-oriented dimension is present (e.g. answer_value with
		// orientation="row"), order the y-axis labels by its values rather than
		// by leaf-array discovery order. Stragglers append at the end.
		return rowHierarchy ? reorderByValues(discovered, rowHierarchy.values) : discovered;
	}, [columns, rowHierarchy]);

	const cellMap = useMemo(() => buildCellMap(columns), [columns]);
	const nSizeMap = useMemo(() => buildNSizeMap(columns), [columns]);
	const seriesMaxMap = useMemo(() => buildSeriesMaxMap(columns, visibleWaves), [columns, visibleWaves]);

	const yAxisActualWidth = useResponsiveYAxisWidth(
		attributes,
		yAxisWidth,
		isEffectiveMultiTile ? MULTI_TILE_Y_MIN : yAxisMinWidth,
		isEffectiveMultiTile ? MULTI_TILE_Y_MAX : yAxisMaxWidth,
		isEffectiveMultiTile ? scaled.scaleFactor : undefined,
	);

	const { width: resolvedYAxisWidth } = useYAxisWidth(attributes, containerWidth, {
		scaleFactor: scaled.scaleFactor,
		isMultiTile: isEffectiveMultiTile,
	});

	// const resolvedYAxisWidth = yAxisActualWidth;

	// Calculate dynamic table height
	// NEW — cap at ASK_KURAL_MAX_CHART_HEIGHT when rendered from AskKural
	const dynamicTableHeight = useMemo(() => {
		if (!contentAreaHeight) return isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : 400;

		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const resolvedXAxisHeight = isEffectiveMultiTile ? 0 : xAxisHeight || 32;

		const availableHeight = contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight;

		if (availableHeight < dimensionsConfig.chart.minHeight) {
			return Math.max(0, availableHeight);
		}

		const maxHeight = isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : dimensionsConfig.chart.maxHeight;

		return Math.min(maxHeight, availableHeight);
	}, [contentAreaHeight, tableHeaderHeight, xAxisHeight, dimensionsConfig.chart.minHeight, dimensionsConfig.chart.maxHeight, isRenderedFromAskKuralInline, isEffectiveMultiTile]);

	// Check if scrolling is needed
	const needsScroll = useMemo(() => {
		if (containerWidth === 0) return false;
		const totalMinWidth = resolvedYAxisWidth + columns.length * dimensionsConfig.column.minWidth;
		return totalMinWidth > containerWidth;
	}, [containerWidth, columns.length, resolvedYAxisWidth, dimensionsConfig.column.minWidth]);

	const minContentWidth = resolvedYAxisWidth + columns.length * dimensionsConfig.column.minWidth;
	const ROW_GAP = attributes.length <= chartConfig.getHorizontalClusteredConfig().rowThreshold ? spacingConfig.gap.rowLarge : spacingConfig.gap.rowSmall;

	const rowHeight = useMemo(() => {
		if (!attributes.length || !dynamicTableHeight) {
			return dimensionsConfig.row.minHeightClustered;
		}

		const rowCount = attributes.length;
		const barCount = maxSeriesPerRow;

		const minRowHeightRequired = barCount * dimensionsConfig.bar.minHeight;
		const availablePerRow = dynamicTableHeight / rowCount;

		const adjustedHeight = availablePerRow - ROW_GAP;
		const computedHeight = Math.max(minRowHeightRequired, Math.floor(adjustedHeight)) + layoutConfig.grid.top + layoutConfig.grid.bottom;

		// Cap row height for AskKuralInline to prevent single-row charts
		// from expanding to fill the entire unconstrained parent
		if (isRenderedFromAskKuralInline) {
			return Math.min(computedHeight, ASK_KURAL_MAX_CHART_HEIGHT);
		}

		// Cap row height to dynamicTableHeight to prevent scrollbar oscillation.
		// When rowHeight exceeds available space, needsYScroll flips true,
		// scrollbar changes layout, contentAreaHeight shifts, causing infinite loop.
		return Math.min(computedHeight, dynamicTableHeight);
	}, [attributes.length, dynamicTableHeight, maxSeriesPerRow, dimensionsConfig.bar.minHeight, dimensionsConfig.row.minHeightClustered, ROW_GAP, isRenderedFromAskKuralInline, layoutConfig.grid.top, layoutConfig.grid.bottom]);

	const needsYScroll = useMemo(() => {
		if (!dynamicTableHeight || !attributes.length) return false;
		const totalContentHeight = attributes.length * rowHeight + (attributes.length - 1) * ROW_GAP;
		return totalContentHeight > dynamicTableHeight;
	}, [dynamicTableHeight, attributes.length, rowHeight, ROW_GAP]);

	const SCROLLBAR_WIDTH = 6;

	const dataColumnWidth = useMemo(() => {
		if (containerWidth === 0 || columns.length === 0) return 0;
		const scrollbarOffset = needsYScroll ? SCROLLBAR_WIDTH : 0;
		const availableForData = containerWidth - resolvedYAxisWidth - scrollbarOffset;
		if (needsScroll) {
			return dimensionsConfig.column.minWidth;
		}
		return Math.floor(availableForData / columns.length);
	}, [containerWidth, columns.length, resolvedYAxisWidth, needsScroll, needsYScroll, dimensionsConfig.column.minWidth]);

	const gridTemplate = useMemo(() => {
		const hClusteredConfig = chartConfig.getHorizontalClusteredConfig();
		const maxYAxisWidth = Math.min(resolvedYAxisWidth, hClusteredConfig.yAxisLabel.maxWidth);
		const frUnit = needsYScroll && !needsScroll ? `minmax(0, 1fr)` : `minmax(${dimensionsConfig.column.minWidth}px, 1fr)`;

		if (needsScroll) {
			return `${maxYAxisWidth}px repeat(${columns.length}, minmax(${dimensionsConfig.column.minWidth}px, 1fr))`;
		} else {
			return `${maxYAxisWidth}px repeat(${columns.length}, ${frUnit})`;
		}
	}, [needsScroll, needsYScroll, resolvedYAxisWidth, columns.length, dimensionsConfig.column.minWidth]);

	// Create border cache
	const getBorders = useBorderGetter(colorsConfig, columns, attributes.length, hierarchyDef.length);

	if (isDataEmpty) {
		return <ChartError errorMessage="No data available" setChartData={() => { }} setIsChartLoading={() => { }} is_retry={false} />;
	}

	return (
		<ChartContainer innerRef={containerRef} scaledPadding={scaled.layout.containerPadding} renderedFrom={renderedFrom}>
			{/* HEADER: Title + Legend */}
			<div ref={headerRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={chartResponse?.chart_config?.ser_color}
					visibleWaves={visibleWaves}
					setVisibleWaves={setVisibleWaves}
					expanded={expanded}
					setExpanded={setExpanded}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={isChartView}
					setIsChartView={setIsChartView}
					showToggle={scaled.layout.showViewToggle}
					scaledTitleFontSize={pxToRem(scaled.typography.title)}
					scaledLegendFontSize={scaled.typography.legend}
					showSubtitle={scaled.layout.showSubtitle}
					showLegend={scaled.layout.showLegend}
					legendLines={scaled.layout.legendLines}
					renderedFrom={renderedFrom}
				/>
			</div>

			{/* CONTENT AREA with ref */}
			{isChartView ? (
				<div ref={contentAreaRef} className="flex flex-1 min-h-0">
					{/* Y-Axis Label */}
					{isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile && (
						<AxisLabel
							label={chartResponse?.y_axis_label || chartResponse?.chart_config?.y_lbl || "Attributes"}
							orientation="vertical"
							maxHeight={contentAreaHeight || undefined}
						/>
					)}

					{/* Scrollable Table Container */}
					<div
						className={`flex-1 relative ${needsScroll ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-auto`}>
						<div
							style={{
								minWidth: needsScroll ? `${minContentWidth}px` : undefined,
								width: "100%",
							}}>
							{/* Headers */}
							<div ref={tableHeaderRef} className="sticky top-0 z-[1000]">
								{!hasHierarchy ? (
									<>
										{/* N-SIZE ROW */}
										<div
											className="grid sticky z-30 bg-white"
											style={{
												gridTemplateColumns: gridTemplate,
												top: 0,
												height: pxToRem(scaled.layout.nSizeRowHeight),
												width: "100%",
											}}>
											<div className="sticky left-0 bg-white z-40" style={{ borderRight: `1px solid ${colorsConfig.borders.gray}` }} />

											{columns.map((col, idx) => {
												const pathKey = col.path.join("|");
												const nSize = nSizeMap.get(pathKey);
												const borders = getBorders("nsize", 0, idx);

												return (
													<div
														key={idx}
														className="flex flex-col justify-center items-center text-center bg-white"
														style={{
															borderTop: `1px solid ${colorsConfig.borders.solid}`,
															borderBottom: `1px solid ${colorsConfig.borders.solid}`,
															...borders,
														}}>
														<NSizeLabel nSize={nSize} fontSize={scaled.typography.nSize} />
													</div>
												);
											})}
										</div>
									</>
								) : (
									<>
										{/* Hierarchy Headers */}
								{hierarchyDef.map((levelDef: any, levelIndex: number) => {
										const headerCells = buildHeaderRow(columns, levelIndex);
										const isFirstLevel = levelIndex === 0;

									return (
												<div
													key={levelIndex}
													className="grid bg-white"
													style={{
														gridTemplateColumns: gridTemplate,
														minHeight: pxToRem(scaled.layout.headerRowHeight),
														width: "100%",
													}}>
													<div className="sticky left-0 bg-white z-40" style={{ borderRight: `1px solid ${colorsConfig.borders.gray}` }} />

													{headerCells.map((cell, idx) => {
														let colIndex = 0;
														for (let k = 0; k < idx; k++) colIndex += headerCells[k].colSpan;

														const borders = getBorders("header", levelIndex, colIndex);

														// ↓ Pass scaleFactor — at 1.0 output is identical to the old string
														const textStyle = getHeaderTextStyle(
															levelIndex + 1,
															!isFirstLevel,
															scaled.scaleFactor, // NEW — was not passed before
														);

														return (
															<div
																key={`${levelIndex}-${idx}`}
																// className carries color + weight; NO dynamic px class
																className={`flex flex-col justify-center items-center text-center bg-white px-1 ${textStyle.className}`}
																style={{
																	gridColumn: `span ${cell.colSpan}`,
																	...borders,
																}}>
														<EllipsisText
																text={cell.label}
																maxLines={isEffectiveMultiTile ? 3 : 4}
																	// ↓ Scaled values replace the previously hardcoded 16
																	lineHeight={textStyle.lineHeight}
																	fontSize={textStyle.fontSize}
																	className="text-center w-full"
																/>
															</div>
														);
													})}
												</div>
											);
										})}

										{/* N-SIZE ROW */}
										<div
											className="grid bg-white"
											style={{
												gridTemplateColumns: gridTemplate,
												height: pxToRem(scaled.layout.nSizeRowHeight),
												width: "100%",
											}}>
											<div className="sticky left-0 bg-white z-40" style={{ borderRight: `1px solid ${colorsConfig.borders.gray}` }} />

											{columns.map((col, idx) => {
												const pathKey = col.path.join("|");
												const nSize = nSizeMap.get(pathKey);
												const borders = getBorders("nsize", 0, idx);

												return (
													<div
														key={idx}
														className="flex items-center justify-center bg-white"
														style={{
															...borders,
															borderBottom: `1px solid ${colorsConfig.borders.solid}`,
														}}>
														{/* ↓ Pass scaled nSize font — NSizeLabel uses it as inline style */}
														<NSizeLabel nSize={nSize} fontSize={scaled.typography.nSize} />
													</div>
												);
											})}
										</div>
									</>
								)}
							</div>

						{/* Data Body */}
						<div style={{
							minHeight: isRenderedFromAskKuralInline ? undefined : dynamicTableHeight,
							maxHeight: isRenderedFromAskKuralInline ? dynamicTableHeight : undefined,
							height: isRenderedFromAskKuralInline ? dynamicTableHeight : undefined,
						}}>
								{attributes.map((attr, rowIndex) => {
									const isAlt = rowIndex % 2 === 1;
									const bgColor = isAlt ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
									const cellPaddingX = isEffectiveMultiTile ? 4 : 16;
									// Budget = column pixel width minus padding on both sides
									const labelTextWidth = resolvedYAxisWidth - cellPaddingX * 2;
									return (
										<div
											key={attr}
											className="grid"
											style={{
												gridTemplateColumns: gridTemplate,
												height: `${rowHeight}px`,
												backgroundColor: bgColor,
											}}>
											{/* Y-axis label cell */}
											<div
												className={`${isEffectiveMultiTile ? "px-1" : "px-4"} font-normal sticky left-0 z-20 flex items-center justify-end`}
												style={{
													color: colorsConfig.text.primary,
													backgroundColor: bgColor,
													borderRight: `1px solid ${colorsConfig.borders.gray}`,
													height: "inherit",
													fontSize: pxToRem(scaled.typography.yAxisLabel),
												}}>
												<RightAlignedEllipsisText
													text={attr}
													maxLines={3}
													lineHeight={scaled.typography.yAxisLabel + 4} // ↓ tracks font size
													maxWidth={labelTextWidth}
												/>
											</div>

											{/* Data cells */}
											{columns.map((col, colIdx) => {
												const fullKey = `${attr}|${col.path.join("|")}`;
												const series = cellMap
													.get(fullKey)
													?.filter((s: any) => visibleWaves?.includes(s.name))
													.map((s: any) => ({
														...s,
														value: s.value,
														n_size: s.n_size,
														stat_marker: s.stat_marker ?? null,
													}));

												const pathKey = col.path.join("|");
												const calculatedMax = seriesMaxMap.get(pathKey);
												const borders = getBorders("data", rowIndex, colIdx);
												const chartHeight = rowHeight;

												return (
													<div key={fullKey} className="pr-2 flex justify-center items-center overflow-hidden" style={borders}>
														{series?.length > 0 && (
															<MiniBarChart
																key={series.map((s: any) => s.value).join("-")}
																series={series}
																waves={chartResponse?.chart_config?.ser_color}
																maxN={calculatedMax}
																labelFormat={chartResponse.chart_config.dlabel_fmt}
																containerHeight={chartHeight}
																containerWidth={dataColumnWidth}
																xAxisDomain={xAxisDomain}
																totalBarsInRow={totalSeriesCount}
																statTestConfig={statTestConfig}
															/>
														)}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>

							{/* X-Axis Scale Row */}
							<div ref={xAxisRef} className="grid sticky bottom-[-0.5px] z-[200] bg-white" style={{ gridTemplateColumns: gridTemplate }}>
								<div
									className="sticky left-0 z-[200] bg-white"
								// style={{ borderRight: `1px solid ${colorsConfig.borders.gray}` }}
								/>
								{columns.map((_, idx) => (
									<div
										key={`xaxis-${idx}`}
										className="pr-2"
										style={{
											borderTop: `1px solid ${colorsConfig.borders.solid}`,
										}}>
										{(!isEffectiveMultiTile || isRenderedFromAskKural) && (
											<XAxisScale
												paddingLeft={xAxisDomain.min < 0 ? layoutConfig.grid.right : 0}
												paddingRight={layoutConfig.grid.right}
												domain={xAxisDomain}
												labelFormat={labelFormat}
												formatDataLabel={(a, b, c) => formatDataLabel(a, b, c, false)}
										tickCount={2}
											ticksCentered={false}
											/>
										)}
									</div>
								))}
							</div>
						</div>
					</div>
					<div
						style={{
							height: `calc(100% - ${xAxisHeight + (needsScroll ? 6 : 0)}px)`,
							borderRight: `1px solid ${colorsConfig.borders.solid}`,
						}}
					/>
				</div>
			) : (
				<div
					ref={tableViewContainerRef}
					style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", justifyContent: "center", alignItems: "center" }}>
					<ChartTabularView
						chartData={apiData}
						attributes={attributes}
						chartType="h_cluster_bar"
						tableContainerHeight={tableViewContainerHeight}
						parentContainerWidth={containerWidth}
						labelFormat={chartResponse?.chart_config?.dlabel_fmt}
						statTestConfig={statTestConfig}
						renderedFrom={renderedFrom}
						filterHierarchy={hierarchyDef}
						seriesOrder={seriesOrder}
					/>
				</div>
			)}

			{/* X-Axis Label */}
			{isChartView && chartResponse?.x_axis_label && !isEffectiveMultiTile && (
				<AxisLabel label={chartResponse?.x_axis_label || chartResponse?.chart_config?.x_lbl} orientation="horizontal" className="mt-2" />
			)}

			{/* Footnote */}
			<div ref={footnoteRef}>
				<ChartFootnote
					src={chartResponse.footnote?.src}
					analysis_name={chartResponse.analysis_type}
					footnoteObj={chartResponse.footnote}
					hasLowNSize={showLowNSize}
					isWeighted={chartResponse.is_weight}
					stat_message={chartResponse.footnote?.stat_message}
					valueParams={chartResponse?.value_params}
					className="py-2 mt-auto"
					renderedFrom={renderedFrom}
				/>
			</div>
		</ChartContainer>
	);
};

export default HClusteredBarChart;
